(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.customElements && !root.customElements.get('curated-selector')) {
    root.customElements.define('curated-selector', api.CuratedSelector);
  }
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  const mobileOwners = new Set();
  const quantity = value => Math.max(0, Math.floor(Number(value) || 0));
  const variantOf = row => row.variants.find(variant => String(variant.id) === String(row.variantId));

  function chooseVariant(variants, preferredId, price) {
    const available = variants.filter(variant => variant.available);
    return available.find(variant => String(variant.id) === String(preferredId)) ||
      available.find(variant => price != null && Number(variant.price) === Number(price)) || available[0] || null;
  }

  function createState(groups) {
    const state = Object.create(null);
    groups.forEach(group => {
      const products = (group.products || []).map(product => ({ ...product, source: 'main' }));
      const recommendations = (group.recommendations || []).map(product => ({ ...product, source: 'recommendation', slot: `rec_${group.id}_${product.id}`, quantity: 0 }));
      state[String(group.id)] = [...products, ...recommendations].map(product => {
        const variants = (product.variants || []).map(variant => ({ ...variant }));
        const selected = chooseVariant(variants, product.variantId, product.price);
        return { ...product, groupId: String(group.id), variants, variantId: selected ? String(selected.id) : '', quantity: selected ? quantity(product.quantity) : 0 };
      });
    });
    return state;
  }

  function aggregateItems(rows) {
    const items = new Map();
    rows.forEach(row => {
      const amount = quantity(row.quantity);
      if (!amount || !row.variantId) return;
      const id = String(row.variantId);
      items.set(id, (items.get(id) || 0) + amount);
    });
    return Array.from(items, ([id, amount]) => ({ id, quantity: amount }));
  }

  function cartQuantity(cart, id) {
    return (cart.items || []).reduce((sum, item) => sum + (String(item.variant_id || item.id) === String(id) ? quantity(item.quantity) : 0), 0);
  }

  function validateSelection(rows, cart, freshProducts) {
    const items = aggregateItems(rows);
    if (!items.length) throw new Error('empty');
    items.forEach(item => {
      const matching = rows.filter(row => String(row.variantId) === item.id && row.quantity > 0);
      matching.forEach(row => {
        const fresh = freshProducts.get(row.url);
        const variant = fresh && fresh.variants.find(entry => String(entry.id) === item.id);
        const configured = variantOf(row);
        if (!variant || !variant.available || !configured) throw new Error('unavailable');
        // Never silently switch an edited variant or charge a changed preview price.
        if (Number(variant.price) !== Number(configured.price)) throw new Error('changed');

        const inventory = variant.inventory_quantity != null ? variant.inventory_quantity : variant.inventory;
        // The freshly rendered section supplies counts missing from product.js.
        const ceiling = inventory != null ? Number(inventory) : configured.inventory;
        const managed = variant.inventory_management !== undefined ? Boolean(variant.inventory_management) : configured.inventory != null;
        if (managed && (!Number.isFinite(Number(ceiling)) || item.quantity + cartQuantity(cart, item.id) > Number(ceiling))) {
          throw new Error('inventory');
        }
      });
    });
    return items;
  }

  function preview(rows, config, deliveryApi, footerApi, packagingApi) {
    const subtotal = source => rows.reduce((sum, row) => sum + ((row.source === 'recommendation') === (source === 'recommendation') ? quantity(row.quantity) * Number((variantOf(row) || {}).price || 0) : 0), 0);
    const products = subtotal('main');
    const recommendations = subtotal('recommendation');
    const merchandise = products + recommendations;
    const cart = { total_price: merchandise, items: [] };
    const minimum = Number(config.minimum != null ? config.minimum : config.minimumCents);
    const delivery = deliveryApi.getDeliveryState(cart, null);
    const eligible = merchandise >= minimum;
    const shipping = eligible ? delivery.currentShippingCost : 0;
    const footer = footerApi ? footerApi.getFooterState(cart, minimum, null) : { shippingCost: shipping, displayTotal: merchandise + shipping, eligible };
    const packagingConfig = config.packaging || {};
    const physical = rows.filter(row => row.quantity > 0 && ((variantOf(row) || {}).requiresShipping ?? row.requiresShipping) !== false);
    const frozen = packagingConfig.enabled !== false && physical.some(row => packagingApi && packagingApi.isFrozenType(row.type || row.productType));
    const cooled = packagingConfig.enabled !== false && physical.some(row => packagingApi && packagingApi.isCooledType(row.type || row.productType));
    const packaging = (frozen ? Number(packagingConfig.frozenPrice || 0) : 0) + (cooled ? Number(packagingConfig.cooledPrice || 0) : 0);
    // Match the normal drawer's preliminary total; packaging is handled in cart.
    return { products, recommendations, shipping: footer.shippingCost, packaging, total: footer.displayTotal, delivery, eligible: footer.eligible };
  }

  // Each displayed cost tier owns one equal segment; the free tier is complete.
  function shippingProgress(tiers, amount) {
    if (!tiers.length) return 0;
    amount = Math.max(0, Number(amount) || 0);
    const index = tiers.findIndex(tier => amount < tier.end);
    if (index < 0 || !tiers[index].cost) return 100;
    const tier = tiers[index];
    const fraction = Math.max(0, Math.min(1, (amount - tier.start) / (tier.end - tier.start)));
    return (index + fraction) / tiers.length * 100;
  }

  class CuratedSelector extends (root ? root.HTMLElement : class {}) {
    connectedCallback() {
      if (this.controller) return;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.config.sectionId = this.config.sectionId || this.dataset.sectionId;
      this.state = this.state || createState(this.config.groups);
      this.activeGroup = this.activeGroup || String((this.config.groups[0] || {}).id || '');
      this.controller = new root.AbortController();
      this.mountedPath = root.location.pathname;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      root.addEventListener('scroll', () => this.updateMobile(), { ...options, passive: true });
      root.addEventListener('resize', () => { this.render(); this.updateMobile(); }, options);
      root.addEventListener('pagehide', () => this.setMobile(false), options);
      root.addEventListener('pageshow', () => this.updateMobile(), options);
      root.document.addEventListener('shopify:section:unload', event => {
        if (event.target === this || event.target.contains(this)) this.disconnectedCallback();
      }, options);
      this.render();
      this.updateMobile();
      if (root.FrostixCuratedPagePolish) root.FrostixCuratedPagePolish.mount(this);
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.setMobile(false);
      if (root.FrostixCuratedPagePolish) root.FrostixCuratedPagePolish.unmount(this);
    }

    rows() {
      // Hidden recommendations keep their edits, but never participate in totals/add.
      const limit = root.matchMedia('(max-width: 749px)').matches ? 6 : 8;
      let recommendations = 0;
      return (this.state[this.activeGroup] || []).filter(row => row.source !== 'recommendation' || ++recommendations <= limit);
    }

    money(cents) {
      return new Intl.NumberFormat(root.document.documentElement.lang || 'de', { style: 'currency', currency: this.config.currency || 'EUR' }).format(cents / 100).replace(/\s+(?=€)/g, '');
    }

    label(key, fallback) {
      const aliases = { error: 'error_label', empty: 'empty_label', inventory: 'stock_label', unavailable: 'stock_label', stock: 'stock_label',
        shippingNext: 'shipping_progress', shippingMinimum: 'shipping_progress', shippingFree: 'shipping_free', afterAddError: 'after_add_error_label' };
      const labels = this.config.labels || {};
      return labels[key] || labels[aliases[key]] || fallback;
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      if (error) {
        error.textContent = message || '';
        error.hidden = !message;
        error.setAttribute('role', 'alert');
      }
    }

    selectGroup(id, focus) {
      if (this.busy || !this.state[id]) return;
      this.activeGroup = id;
      this.showError('');
      this.render();
      if (focus) this.querySelectorAll('[data-group]').forEach(tab => { if (tab.dataset.group === id) tab.focus(); });
    }

    onClick(event) {
      const button = event.target.closest('[data-group], [data-action], [data-add-selection]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (this.busy) return;
      if (button.hasAttribute('data-add-selection')) { void this.addSelection(button); return; }
      if (button.hasAttribute('data-group')) { this.selectGroup(button.dataset.group); return; }
      const card = button.closest('[data-slot]');
      if (!card) return;
      const row = this.rows().find(entry => String(entry.slot) === card.dataset.slot);
      if (!row) return;
      const delta = { increase: 1, decrease: -1 }[button.dataset.action];
      if (!delta) return;
      row.quantity = Math.max(0, row.quantity + delta);
      this.showError('');
      this.render();
    }

    onChange(event) {
      const select = event.target.closest('[data-variant]');
      if (!select || this.busy || !this.contains(select)) return;
      const card = select.closest('[data-slot]');
      const row = card && this.rows().find(entry => String(entry.slot) === card.dataset.slot);
      if (!row) return;
      const variant = row.variants.find(entry => String(entry.id) === select.value && entry.available);
      if (variant) row.variantId = String(variant.id);
      this.showError('');
      this.render();
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-group]');
      if (!tab || this.busy) return;
      const tabs = Array.from(this.querySelectorAll('[data-group]'));
      const index = tabs.indexOf(tab);
      const targets = { ArrowRight: (index + 1) % tabs.length, ArrowLeft: (index + tabs.length - 1) % tabs.length, Home: 0, End: tabs.length - 1 };
      if (!(event.key in targets)) return;
      event.preventDefault();
      this.selectGroup(tabs[targets[event.key]].dataset.group, true);
    }

    renderShippingTiers(deliveryApi, products) {
      const container = this.querySelector('[data-shipping-tiers]');
      const tiers = [];
      let amount = 0;
      // Discover boundaries through the shared source, not a second tier table.
      for (let index = 0; index < 10; index++) {
        const state = deliveryApi.getDeliveryState({ total_price: amount, items: [] }, null);
        const previous = tiers[tiers.length - 1];
        if (previous && previous.cost === state.currentShippingCost) previous.end = state.rangeEnd;
        else tiers.push({ start: amount, end: state.rangeEnd, cost: state.currentShippingCost });
        if (!state.currentShippingCost || state.rangeEnd <= amount) break;
        amount = state.rangeEnd;
      }
      if (container) container.replaceChildren(...tiers.map((tier, index) => {
        const tile = root.document.createElement('span');
        const template = index === 0 ? this.label('shipping_under', '__AMOUNT__') : this.label('shipping_from', '__AMOUNT__');
        const threshold = template.replace('__AMOUNT__', this.money(index === 0 ? tier.end : tier.start));
        const price = tier.cost ? this.money(tier.cost) : this.label('shippingFree', this.money(0));
        const thresholdLabel = root.document.createElement('span');
        const priceLabel = root.document.createElement('span');
        thresholdLabel.textContent = threshold;
        priceLabel.textContent = price;
        tile.append(thresholdLabel, priceLabel);
        tile.classList.toggle('is-active', products >= tier.start && (index === tiers.length - 1 || products < tier.end));
        return tile;
      }));
      return tiers;
    }

    render() {
      const group = this.config.groups.find(entry => String(entry.id) === this.activeGroup);
      this.setAttribute('aria-busy', String(Boolean(this.busy)));
      this.querySelectorAll('[data-group]').forEach(tab => {
        const active = tab.dataset.group === this.activeGroup;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        tab.disabled = Boolean(this.busy);
      });
      this.querySelectorAll('[data-pane]').forEach(pane => {
        const active = pane.dataset.pane === this.activeGroup;
        pane.hidden = !active;
        if (!active) return;
        pane.querySelectorAll('[data-slot]').forEach(card => {
          const row = (this.state[this.activeGroup] || []).find(entry => String(entry.slot) === card.dataset.slot);
          if (!row) return;
          card.hidden = !this.rows().includes(row);
          if (card.hidden) return;
          const variant = variantOf(row);
          card.querySelectorAll('[data-variant]').forEach(select => {
            if (select.type === 'radio') select.checked = select.value === row.variantId;
            else select.value = row.variantId;
            select.disabled = Boolean(this.busy) || !variant;
          });
          card.querySelectorAll('[data-quantity]').forEach(node => { node.textContent = row.quantity; });
          const price = Number((variant || {}).price || 0);
          const compare = Number((variant || {}).compareAtPrice || 0);
          const sale = compare > price;
          card.querySelectorAll('[data-price]').forEach(node => { node.textContent = this.money(price); });
          card.querySelectorAll('[data-compare-price]').forEach(node => {
            node.textContent = sale ? this.money(compare) : '';
            node.hidden = !sale;
          });
          card.querySelectorAll('[data-price-container]').forEach(node => { node.classList.toggle('is-sale', sale); });
          card.querySelectorAll('[data-product-popup]').forEach(button => {
            const url = new URL(row.url, root.location.href);
            url.searchParams.set('variant', row.variantId);
            button.setAttribute('data-product-url', url.pathname + url.search);
          });
          card.querySelectorAll('[data-stepper]').forEach(node => { node.hidden = row.quantity === 0; });
          card.querySelectorAll('[data-select-product]').forEach(node => { node.hidden = row.quantity > 0; });
          card.querySelectorAll('[data-action]').forEach(button => {
            button.disabled = Boolean(this.busy) || !variant || (button.dataset.action === 'decrease' && row.quantity === 0);
          });
        });
      });
      const title = this.querySelector('[data-summary-title]');
      if (title && group) title.textContent = group.title;
      const footerApi = root.FrostixCartFooterState;
      const deliveryApi = root.FrostixCartDeliveryState;
      if (deliveryApi) {
        const totals = preview(this.rows(), this.config.shipping || {}, deliveryApi, footerApi, root.PackagingUtils);
        const amount = totals.products + totals.recommendations;
        const tiers = this.renderShippingTiers(deliveryApi, amount);
        const progressPercent = shippingProgress(tiers, amount);
        this.querySelectorAll('[data-summary]').forEach(node => {
          if (typeof totals[node.dataset.summary] === 'number') node.textContent = this.money(totals[node.dataset.summary]);
        });
        this.querySelectorAll('[data-mobile-total]').forEach(node => { node.textContent = this.money(totals.total); });
        const progress = this.querySelector('[data-shipping-progress]');
        if (progress) {
          progress.style.setProperty('--progress', progressPercent + '%');
          progress.style.width = progressPercent + '%';
          const track = progress.closest('[role="progressbar"]') || progress;
          track.setAttribute('role', 'progressbar');
          track.setAttribute('aria-valuemin', '0');
          track.setAttribute('aria-valuemax', '100');
          track.setAttribute('aria-valuenow', String(progressPercent));
        }
      }
      this.querySelectorAll('[data-add-selection]').forEach(button => {
        button.disabled = Boolean(this.busy) || !aggregateItems(this.rows()).length || !deliveryApi;
        button.setAttribute('aria-busy', String(Boolean(this.busy)));
        const text = this.busy ? this.config.labels.loading_label : this.config.labels.add_label;
        if (text) button.textContent = text;
      });
    }

    setMobile(active) {
      this.querySelectorAll('[data-mobile-bar]').forEach(bar => { bar.hidden = !active; });
      if (active) mobileOwners.add(this);
      else mobileOwners.delete(this);
      [root.document.documentElement, root.document.body].forEach(node => node.classList.toggle('curated-selector-mobile-active', mobileOwners.size > 0));
    }

    updateMobile() {
      this.setMobile(Boolean(this.controller && this.isConnected && this.mountedPath === root.location.pathname && root.matchMedia('(max-width: 749px)').matches && this.getBoundingClientRect().top <= 0));
    }

    async readJson(url, options = {}) {
      const response = await root.fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, headers: { Accept: 'application/json', ...options.headers } });
      const body = await response.json();
      if (!response.ok || body.status >= 400) throw new Error(body.description || body.message || 'request');
      return body;
    }

    readAuthoritativeCart(url) {
      // CartStore's fetch guard serves displayCart; refresh() hides read failures.
      return new Promise((resolve, reject) => {
        const xhr = new root.XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('Cache-Control', 'no-cache');
        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300) { reject(new Error('request')); return; }
          try {
            const cart = JSON.parse(xhr.responseText);
            if (!cart || !Array.isArray(cart.items) || cart.item_count === undefined) throw new Error('cart');
            resolve(cart);
          } catch (error) { reject(error); }
        };
        xhr.onerror = xhr.onabort = xhr.ontimeout = () => reject(new Error('request'));
        xhr.send();
      });
    }

    async readFreshConfig() {
      if (!this.config.sectionId) throw new Error('stock');
      const url = new URL(root.location.href);
      url.searchParams.set('section_id', this.config.sectionId);
      const response = await root.fetch(url.pathname + url.search, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('stock');
      const document = new root.DOMParser().parseFromString(await response.text(), 'text/html');
      const script = document.querySelector('curated-selector [data-config]');
      if (!script) throw new Error('stock');
      return JSON.parse(script.textContent);
    }

    async addSelection(trigger) {
      if (this.busy || !aggregateItems(this.rows()).length) return;
      this.busy = true;
      this.showError('');
      this.render();
      let submitted = false;
      try {
        const drawer = root.document.querySelector('cart-drawer');
        if (!drawer || !drawer.getSectionsToRender || !drawer.renderContents || !drawer.open) throw new Error('drawer');
        // Do not flush unrelated optimistic card edits as part of this selection.
        if (root.CartStore && root.CartStore.hasPending && root.CartStore.hasPending()) throw new Error('pending');
        const routes = root.routes || {};
        if (!routes.cart_url || !routes.cart_add_url) throw new Error('routes');
        const controller = this.controller;
        const rows = this.rows().filter(row => row.quantity > 0).map(row => ({ ...row }));
        const productUrls = [...new Set(rows.map(row => row.url))];
        const productJsonUrl = url => {
          const parsed = new URL(url, root.location.origin);
          if (parsed.origin !== root.location.origin || !parsed.pathname.includes('/products/')) throw new Error('product');
          return parsed.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
        };
        const [cart, freshConfig, ...products] = await Promise.all([
          this.readAuthoritativeCart(routes.cart_url + '.js'),
          this.readFreshConfig(),
          ...productUrls.map(url => this.readJson(productJsonUrl(url)))
        ]);
        if (freshConfig.currency !== this.config.currency) throw new Error('changed');
        const freshGroup = freshConfig.groups.find(group => String(group.id) === this.activeGroup);
        if (!freshGroup) throw new Error('unavailable');
        rows.forEach(row => {
          const stockRows = row.source === 'recommendation' ? freshGroup.recommendations || [] : freshGroup.products || [];
          const stock = stockRows.find(product => String(product.id) === String(row.id) && String(product.slot) === String(row.slot));
          const product = products[productUrls.indexOf(row.url)];
          const selected = product.variants.find(variant => String(variant.id) === String(row.variantId));
          const stockVariant = stock && stock.variants.find(variant => String(variant.id) === String(row.variantId));
          if (!selected || !stockVariant || !stockVariant.available) throw new Error('unavailable');
          if (!Object.prototype.hasOwnProperty.call(stockVariant, 'inventory')) throw new Error('stock');
          selected.inventory = stockVariant.inventory;
          selected.inventory_quantity = stockVariant.inventory;
          selected.policy = stockVariant.policy;
          selected.inventory_management = stockVariant.inventory == null ? null : 'managed';
          row.requiresShipping = stockVariant.requiresShipping ?? row.requiresShipping;
        });
        const items = validateSelection(rows, cart, new Map(productUrls.map((url, index) => [url, products[index]])));
        const packaging = root.PackagingUtils;
        if (packaging) {
          const config = packaging.getConfig();
          const packagingIds = new Set();
          if (config) rows.forEach(row => {
            const product = products[productUrls.indexOf(row.url)];
            packaging.getPackagingItemsForAdd(cart, config.frozenId, config.cooledId, product.type || row.type || row.productType, row.requiresShipping !== false)
              .forEach(id => packagingIds.add(String(id)));
          });
          packagingIds.forEach(id => { if (!items.some(item => item.id === id)) items.push({ id, quantity: 1 }); });
        }
        if (!controller || controller !== this.controller || controller.signal.aborted) return;
        // A resize during preflight must not submit now-dormant desktop choices.
        const visibleSlots = new Set(this.rows().map(row => row.slot));
        if (rows.some(row => !visibleSlots.has(row.slot))) throw new Error('changed');
        if (root.CartStore && root.CartStore.hasPending && root.CartStore.hasPending()) throw new Error('pending');
        const sections = drawer.getSectionsToRender().map(section => section.id);
        submitted = true;
        const result = await this.readJson(routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: root.location.pathname })
        });
        const verified = await this.readAuthoritativeCart(routes.cart_url + '.js');
        if (items.some(item => cartQuantity(verified, item.id) < cartQuantity(cart, item.id) + item.quantity)) throw new Error('verification');
        if (!result.sections || sections.some(section => !result.sections[section])) throw new Error('sections');
        if (root.CartStore) {
          // This store has no public cart replacement/notify API; reuse its consumer seam.
          root.CartStore.serverCart = verified;
          if (root.CartStore._notify) root.CartStore._notify();
        }
        if (controller !== this.controller) return;
        drawer.setActiveElement && drawer.setActiveElement(trigger);
        drawer.renderContents(result);
        drawer.open(trigger);
      } catch (error) {
        if (this.controller) {
          this.showError(submitted ? this.label('afterAddError', 'Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst. Die Produkte könnten bereits hinzugefügt worden sein.') :
            this.label(error.message, this.label('error', 'Die Auswahl konnte nicht hinzugefügt werden. Bitte prüfe die Verfügbarkeit.')));
        }
      } finally {
        this.busy = false;
        if (this.controller) this.render();
      }
    }
  }

  return { chooseVariant, createState, aggregateItems, validateSelection, preview, shippingProgress, CuratedSelector };
});
